import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../../config";
import { debounce } from "lodash";
import MainBody from "../../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../../components/Pagination";
import sortCaret from "../../../../components/SortCaret";
import AddEditSection from "./AddEditSection";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import SectionActionButton from "./SectionActionButton";
import Confirmation from "../../../../components/Confirmation";
import { useOrganization } from "../../../../lib/context/OrganisationContext";
import Questions from "../Questions/Questions";
import { CaretDownIcon, CaretRightIcon } from "@radix-ui/react-icons";
import AdminFilters from "../../../../components/AdminFilters";

const Sections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [sectionsList, setSectionsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { organizationData } = useOrganization();

  const [sectionId, setSectionId] = useState(null);
  const [showAddEditSection, setShowAddEditSection] = useState(false);
  const [showSectionDelete, setShowSectionDelete] = useState(false);

  const [versionId, setVersionId] = useState();
  const [sectionType, setSectionType] = useState();

  const [organizationTypeId, setOrganizationTypeId] = useState();

  const toggleShowAddEditSection = () => {
    if (showAddEditSection) {
      setEdit(false);
      setSectionId(null);
    }
    setShowAddEditSection(!showAddEditSection);
  };

  const toggleShowDelete = () => {
    if (showSectionDelete) {
      setSectionId(null);
    }
    setShowSectionDelete(!showSectionDelete);
  };

  const fetchSection = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_SECTIONS}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&organization_type_id=${organizationTypeId ? organizationTypeId : ""}&version_id=${
            versionId ? versionId : ""
          }&section_type=${sectionType ? sectionType : ""}`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const sectionsList = data.data.data;
        setSectionsList(sectionsList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setSectionsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, versionId, organizationTypeId, sectionType]
  );

  const sectionsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: sectionsList[i]?.id,
        id: sectionsList[i]?.id,
        organization_type: sectionsList[i]?.organization_type?.name
          ? sectionsList[i]?.organization_type?.name
          : "",
        version_number: sectionsList[i]?.version?.version_number
          ? `${sectionsList[i]?.version?.version_number} - ${sectionsList[i]?.version?.name}`
          : "",
        order: sectionsList[i]?.order ? sectionsList[i]?.order : "",
        title: sectionsList[i]?.title ? sectionsList[i]?.title : "",
        sub_title: sectionsList[i]?.sub_title
          ? sectionsList[i]?.sub_title
          : "-",
        section_type: sectionsList[i]?.section_type
          ? sectionsList[i]?.section_type
          : "",
        yes_color: (
          <div className="flex items-center justify-center w-full">
            <div
              style={{
                background: sectionsList[i]?.yes_color
                  ? sectionsList[i]?.yes_color
                  : "#ff0000",
              }}
              className={`h-6 w-6 rounded-md`}
            />
          </div>
        ),
        no_color: (
          <div className="flex items-center justify-center w-full">
            <div
              style={{
                background: sectionsList[i]?.no_color
                  ? sectionsList[i]?.no_color
                  : "#007718",
              }}
              className={`h-6 w-6 rounded-md`}
            />
          </div>
        ),
        questions: sectionsList[i]?.questions ? sectionsList[i]?.questions : [],
        is_used: sectionsList[i]?.is_used,
        action: (
          <>
            <SectionActionButton
              id={sectionsList[i]?.id}
              setEdit={setEdit}
              setSectionId={setSectionId}
              toggleShowAddEditSection={toggleShowAddEditSection}
              toggleShowDelete={toggleShowDelete}
              is_used={sectionsList[i]?.is_used}
            />
          </>
        ),
      });
    }
    return items;
  };

  const sections = sectionsGenerator(sectionsList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "organization_type",
      text: "Industry Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "version_number",
      text: "Version Number",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "order",
      text: "Order",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "title",
      text: "Title",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "sub_title",
      text: "Sub Title",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "section_type",
      text: "Section Type",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "yes_color",
      text: "Yes Color",
      align: "center",
      headerAlign: "center",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "no_color",
      text: "No Color",
      align: "center",
      headerAlign: "center",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?tab=risk-assessment&childTab=sections&limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchSection();
  }, [fetchSection]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchSection(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchSection(limit, page);
  };

  // Single Section Delete
  const handleSectionDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.ADMIN_SECTION_DELETE}/${sectionId}`);
      setLoading(false);
      fetchSection();
    } catch (error) {
      setLoading(false);
    }
  };

  const expandRow = {
    renderer: (row) => (
      <>
        <Questions expandList={true} questionData={row?.questions} />
      </>
    ),
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            <AdminFilters
              organizationType={true}
              setOrganizationTypeId={setOrganizationTypeId}
              organizationTypeId={organizationTypeId}
              version={true}
              setVersionId={setVersionId}
              versionId={versionId}
              sectionTypeSelect={true}
              setSectionType={setSectionType}
              sectionType={sectionType}
            />
            {/* Add Section */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Section" : "Add Section"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setSectionId(null);
                setShowAddEditSection(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Section</span>
                </div>
              }
              className={"sm:max-w-1/2"}
              open={showAddEditSection}
              buttonClassName={""}
              setOpen={setShowAddEditSection}
            >
              <AddEditSection
                edit={edit}
                toggleShowAddEditSection={toggleShowAddEditSection}
                id={sectionId}
                orgId={
                  organizationData?.organizationId
                    ? organizationData?.organizationId
                    : ""
                }
                fetchSectionData={() => fetchSection(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        innerBody={true}
        expandList={true}
        className="p-0"
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class max-h-[550px]">
            <BootstrapTable
              keyField="key"
              remote
              data={sections}
              columns={columns}
              expandRow={expandRow}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Section"}
        open={showSectionDelete}
        setOpen={setShowSectionDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete section?"}
        okAction={async () => {
          handleSectionDelete();
          toggleShowDelete();
          fetchSection();
        }}
        cancelAction={() => {
          setSectionId(null);
          setShowSectionDelete(!showSectionDelete);
        }}
      />
    </>
  );
};

export default Sections;
